import { Injectable } from '@angular/core';
import {
    BaseHttpService,
    Create, Delete,
    GetById, Patch,
    Update,
    UriBuilderParams
} from '@app/core/services/base/base-http.service';
import { HttpServiceBundle } from '@app/core/services/base/http-service-bundle.service';
import { Observable } from 'rxjs';
import { DrQueryParams, OP, OpData } from '@app/core/services/base/base-service-with-bundle.service';
import { map } from 'rxjs/operators';
import { AuthenticationMethod, ClientUser, ClientUserQueryResult } from '@app/shared/models/client-user';
import { DRResponse } from '@app/shared/models/response';
import { BackendUriUnits } from '@app/core/services/base/backend-uri-units';
import { Department } from '@app/shared/models/department';
import { Client } from '@app/shared/models/client';
import { BaseWithIdList } from '@app/shared/models/list';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { SimpleInputDialogComponent } from '@app/shared/dialogs/simple-input-dialog/simple-input-dialog.component';
import { HttpParams } from '@angular/common/http';
import { CustomHttpParamEncoder } from '../helpers/param-encoder';

export class ClientUserUriBuilderParams extends UriBuilderParams<ClientUser> {
    private readonly _client: string;
    private readonly _department: string;

    get client() {
        return this._client;
    }

    get department() {
        return this._department;
    }

    constructor(parent: Client | Department, user?: ClientUser | string) {
        super(user);
        if (parent instanceof Client) {
            this._client = this.getParamId(parent);
        } else if (parent instanceof Department) {
            this._department = this.getParamId(parent);
        }
    }

    public buildUri(): string {
        if (this.department) {
            let uri = `/${BackendUriUnits.CLIENTS}/${BackendUriUnits.SETTINGS}/${BackendUriUnits.DATA}/${BackendUriUnits.DEPARTMENTS}/${this.department}`;
            uri += this.simpleUriBuild(BackendUriUnits.USERS);
            return uri;
        } else if (this.client)  {
            let uri = `/${BackendUriUnits.CLIENTS}/${BackendUriUnits.MEMBER}/${BackendUriUnits.CLIENTS}/${this._client}`;
            uri += this.simpleUriBuild(BackendUriUnits.USERS);
            return uri;
        }
    }
}

@Injectable({ providedIn: 'root' })
export class UserService extends BaseHttpService<ClientUser, ClientUserUriBuilderParams>
                         implements GetById<ClientUser, ClientUserUriBuilderParams>,
                                    Create<ClientUser, ClientUserUriBuilderParams>,
                                    Update<ClientUser, ClientUserUriBuilderParams>,
                                    Patch<ClientUser, ClientUserUriBuilderParams>,
                                    Delete<ClientUser, ClientUserUriBuilderParams> {

    constructor(httpServiceBundle: HttpServiceBundle) {
        super(ClientUser, httpServiceBundle, true);
    }

    // BASE METHODS ****************************************************************************************

    public getById(uriParams: ClientUserUriBuilderParams): Observable<ClientUser> {
        return this.getBaseById(uriParams);
    }

    public create(uriParams: ClientUserUriBuilderParams, userToCreate: ClientUser): Observable<ClientUser> {
        return this.createBase(uriParams, userToCreate);
    }

    public update(uriParams: ClientUserUriBuilderParams, userToUpdate: ClientUser, params?: DrQueryParams): Observable<ClientUser> {
        return this.updateBase(uriParams, userToUpdate, params);
    }

    public patch(uriParams: ClientUserUriBuilderParams, data: OpData[]): Observable<ClientUser> {
        return this.patchBase(uriParams, data);
    }

    public patchAndUpdate(uriParams: ClientUserUriBuilderParams, userToUpdate: ClientUser, data: OpData[]): Observable<ClientUser> {
        return this.patchAndUpdateBase(uriParams, userToUpdate, data);
    }

    public delete(uriParams: ClientUserUriBuilderParams): Observable<boolean> {
        return this.deleteBase(uriParams);
    }


    // EXTRA METHODS ****************************************************************************************

    public query(uriParams: ClientUserUriBuilderParams, params?: DrQueryParams): Observable<ClientUserQueryResult | BaseWithIdList<ClientUser>> {
        const uri = `${this.uri}${uriParams.buildUri()}`;
        return this.http.get<DRResponse<ClientUserQueryResult | BaseWithIdList<ClientUser>>>(uri, params && params.queryParams)
                        .pipe(map(result  =>  uriParams.department ? new BaseWithIdList(ClientUser, result.data) : new ClientUserQueryResult(result.data)));
    }

    public switchTwoFAMethod(uriParams: ClientUserUriBuilderParams, method: AuthenticationMethod | null) {
        const uri = `${this.uri}/${BackendUriUnits.CLIENTS}/${BackendUriUnits.MFA}/${BackendUriUnits.USERS}/${uriParams.base}`;
        const data: OpData[] = [{
            op: (!method || method == AuthenticationMethod.STANDARD) ? OP.remove : OP.replace,
            path: 'authentication_method',
            value: method
        }];

        return this.http.patch<any>(uri, data)
            .pipe(map(result => result.data));
    }

    public deactivateTwoFA(uriParams: ClientUserUriBuilderParams) {
        return this.switchTwoFAMethod(uriParams, AuthenticationMethod.STANDARD);
    }

    public setupTwoFAWithSms(uriParams: ClientUserUriBuilderParams, phoneNumber: string): Observable<string> {

        const uri = this.constructUri(uriParams, { urlSuffix: `authentication/${AuthenticationMethod.SMS}` });
        
        let encoder = new CustomHttpParamEncoder();
        let queryParams = new HttpParams({
            fromObject: {
                sms_phone_number: phoneNumber
            },
            encoder: encoder
        },);

        return this.http.put<any>(uri, {}, { params: queryParams })
            .pipe(map(result => result));
    }

    public setupTwoFAWithApp(uriParams: ClientUserUriBuilderParams): Observable<any> {
        
        const uri = this.constructUri(uriParams, { urlSuffix: `authentication/${AuthenticationMethod.TOTP}` });
        
        return this.http.put<any>(uri, {})
            .pipe(map(result => result));
    }

    public verifyTwoFACode(uriParams: ClientUserUriBuilderParams, method: AuthenticationMethod, tan: string): Observable<any> {

        const uri = this.constructUri(uriParams, { urlSuffix: `authentication/${method}/verify` });
        
        let encoder = new CustomHttpParamEncoder();
        let queryParams = new HttpParams({
            fromObject: {
                tan: tan
            },
            encoder: encoder
        },);

        return this.http.put<any>(uri, {}, { params: queryParams })
            .pipe(map(result => result));
    }


    // SHARED ERROR HANDLING

    public promptForTan(): Promise<any> {
        return new Promise((resolve, reject) => {

            const dialogRef = this.dialog.open(SimpleInputDialogComponent, {
                width: '400px',
                data: {
                    title: marker('user_management.dialog.tan.title'),
                    text: marker('user_management.dialog.tan.text'),
                    confirmBtnOverride: marker('common.btn.continue'),
                    closeBtnOverride: marker('common.btn.cancel'),
                    label: marker('user_management.dialog.tan.label')
                }
            });

            dialogRef.afterClosed()
                     .subscribe((dialogResult: { input: string }) => {
                if (dialogResult.input) {
                    resolve(dialogResult.input);
                } else {
                    reject()
                }
            }, () => reject());
        });
    }

    public promptForPassword(): Promise<any> {
        return new Promise((resolve, reject) => {
            const dialogRef = this.dialog.open(SimpleInputDialogComponent, {
                width: '400px',
                data: {
                    title: marker('user_management.dialog.confirm_password.title'),
                    text: marker('user_management.dialog.confirm_password.text'),
                    confirmBtnOverride: marker('common.btn.continue'),
                    closeBtnOverride: marker('common.btn.cancel'),
                    label: marker('user_management.dialog.confirm_password.password'),
                    inputType: 'password'
                }
            });

            dialogRef.afterClosed()
                .subscribe((result: { input: string }) => {
                    if (result.input) {
                        resolve(result.input);
                    } else {
                        reject()
                    }
            }, () => reject());
        });
    }

}
